import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import { mesReferencia } from '../lib/pedidoMensal'
import { supabase } from '../lib/supabase'
import {
  downloadText,
  formatCurrency,
  formatNumber,
  statusContratoClass,
  toCsv,
} from '../lib/utils'
import type { SaldoContrato } from '../types/database'
import { GRUPOS } from '../types/database'

export function ContratoPage() {
  const { canViewValores } = useAuth()
  const hoje = useMemo(() => new Date(), [])
  const [mesAno, setMesAno] = useState({ year: hoje.getFullYear(), month: hoje.getMonth() + 1 })
  const mesRef = mesReferencia(mesAno.year, mesAno.month)
  const [rows, setRows] = useState<SaldoContrato[]>([])
  const [filtroGrupo, setFiltroGrupo] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const tableSort = useTableSort<SaldoContrato>('alimento', 'asc')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase.rpc('saldo_contrato_mes', { p_mes: mesRef })
    if (err) setError(err.message)
    else setRows((data as SaldoContrato[]) || [])
    setLoading(false)
  }, [mesRef])

  useEffect(() => {
    document.title = 'Contrato · ISEN EDUCAE'
    load()
  }, [load])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filtroGrupo && r.grupo !== filtroGrupo) return false
      if (filtroStatus && r.status !== filtroStatus) return false
      return true
    })
  }, [rows, filtroGrupo, filtroStatus])

  const filteredSorted = useMemo(() => {
    const accessors: Record<string, (r: SaldoContrato) => string | number> = {
      alimento: (r) => r.alimento,
      grupo: (r) => r.grupo,
      unidade: (r) => r.unidade,
      qtd_pedida: (r) => Number(r.quantidade_pedida),
      valor_unit: (r) => Number(r.valor_unitario),
      valor_total: (r) => Number(r.valor_total),
      qtd_entregue: (r) => Number(r.quantidade_entregue),
      saldo_qtd: (r) => Number(r.saldo_quantidade),
      saldo_valor: (r) => Number(r.saldo_valor),
      pct: (r) => Number(r.pct_executado),
      status: (r) => r.status,
    }
    return tableSort.sortRows(filtered, accessors)
  }, [filtered, tableSort])

  const kpis = useMemo(() => {
    const valorPedido = filtered.reduce(
      (s, r) => s + Number(r.quantidade_pedida) * Number(r.valor_unitario),
      0,
    )
    const valorEntregue = filtered.reduce(
      (s, r) => s + Number(r.quantidade_entregue) * Number(r.valor_unitario),
      0,
    )
    const saldo = valorPedido - valorEntregue
    const excedidos = filtered.filter((r) => r.status === 'excedido').length
    return {
      valorPedido,
      valorEntregue,
      pct: valorPedido ? (valorEntregue / valorPedido) * 100 : 0,
      saldo,
      excedidos,
    }
  }, [filtered])

  function exportCsv() {
    downloadText(
      `contrato-${mesRef.slice(0, 7)}.csv`,
      toCsv(
        filtered.map((r) => {
          const row: Record<string, string | number> = {
            Mês: mesRef.slice(0, 7),
            Produto: r.alimento,
            Grupo: r.grupo,
            Unidade: r.unidade,
            Pedida: r.quantidade_pedida,
            Entregue: r.quantidade_entregue,
            'Saldo qtd': r.saldo_quantidade,
            '%': r.pct_executado,
            Status: r.status,
          }
          if (canViewValores) {
            row['Valor unit.'] = r.valor_unitario
            row['Valor pedido'] = r.valor_total
            row['Saldo R$'] = r.saldo_valor
          }
          return row
        }),
      ),
    )
  }

  if (loading) return <div className="loading">Carregando contrato…</div>

  return (
    <div>
      <div className="page-header">
        <h1>Contrato</h1>
        <div className="page-actions">
          <button type="button" className="btn btn-secondary" onClick={load}>
            Atualizar
          </button>
          <button type="button" className="btn btn-secondary" onClick={exportCsv}>
            Exportar CSV
          </button>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: '0 0 1rem' }}>
        Quantidade pedida vem do Pedido Mensal do mês filtrado; quantidade entregue das Entregas do
        mesmo período.
      </p>
      <div className="kpi-grid">
        {canViewValores && (
          <>
            <div className="kpi">
              <div className="kpi-label">Valor pedido</div>
              <div className="kpi-value" style={{ fontSize: 20 }}>
                {formatCurrency(kpis.valorPedido)}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Valor entregue</div>
              <div className="kpi-value" style={{ fontSize: 20 }}>
                {formatCurrency(kpis.valorEntregue)}
              </div>
              <div className="kpi-sub">{formatNumber(kpis.pct, 1)}%</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Saldo</div>
              <div className="kpi-value" style={{ fontSize: 20 }}>
                {formatCurrency(kpis.saldo)}
              </div>
            </div>
          </>
        )}
        <div className="kpi">
          <div className="kpi-label">Itens excedidos</div>
          <div className="kpi-value">{kpis.excedidos}</div>
        </div>
      </div>
      <div className="filters">
        <div className="field">
          <label>Mês</label>
          <input
            type="month"
            value={mesRef.slice(0, 7)}
            onChange={(e) => {
              const [y, m] = e.target.value.split('-').map(Number)
              if (y && m) setMesAno({ year: y, month: m })
            }}
          />
        </div>
        <div className="field">
          <label>Grupo</label>
          <select value={filtroGrupo} onChange={(e) => setFiltroGrupo(e.target.value)}>
            <option value="">Todos</option>
            {GRUPOS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos</option>
            {['ok', 'atencao', 'critico', 'excedido', 'zerado'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh
                label="Produto"
                sortKey="alimento"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="name"
              />
              <SortableTh
                label="Grupo"
                sortKey="grupo"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Unid."
                sortKey="unidade"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="compact"
              />
              <SortableTh
                label="Qtd pedida"
                sortKey="qtd_pedida"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="num"
              />
              {canViewValores && (
                <>
                  <SortableTh
                    label="R$ unit."
                    sortKey="valor_unit"
                    activeKey={tableSort.sortKey}
                    direction={tableSort.sortDir}
                    onSort={tableSort.toggleSort}
                    col="currency"
                  />
                  <SortableTh
                    label="R$ pedido"
                    sortKey="valor_total"
                    activeKey={tableSort.sortKey}
                    direction={tableSort.sortDir}
                    onSort={tableSort.toggleSort}
                    col="currency"
                  />
                </>
              )}
              <SortableTh
                label="Qtd entregue"
                sortKey="qtd_entregue"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="num"
              />
              <SortableTh
                label="Saldo qtd"
                sortKey="saldo_qtd"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="num"
              />
              {canViewValores && (
                <SortableTh
                  label="Saldo R$"
                  sortKey="saldo_valor"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="currency"
                />
              )}
              <SortableTh
                label="%"
                sortKey="pct"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="num"
              />
              <SortableTh
                label="Status"
                sortKey="status"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="status"
              />
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((r) => (
              <tr key={r.id}>
                <td className="col-name">{r.alimento}</td>
                <td className="col-text">{GRUPOS.find((g) => g.value === r.grupo)?.label || r.grupo}</td>
                <td className="col-compact">{r.unidade}</td>
                <td className="col-num">{formatNumber(Number(r.quantidade_pedida), 3)}</td>
                {canViewValores && (
                  <>
                    <td className="col-currency">{formatCurrency(Number(r.valor_unitario))}</td>
                    <td className="col-currency">{formatCurrency(Number(r.valor_total))}</td>
                  </>
                )}
                <td className="col-num">{formatNumber(Number(r.quantidade_entregue), 3)}</td>
                <td className="col-num">{formatNumber(Number(r.saldo_quantidade), 3)}</td>
                {canViewValores && (
                  <td className="col-currency">{formatCurrency(Number(r.saldo_valor))}</td>
                )}
                <td className="col-num">{formatNumber(Number(r.pct_executado), 1)}%</td>
                <td className="col-status">
                  <span className={statusContratoClass(r.status)}>{r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
